import React, { useState, useRef } from "react";
import { api, formatErr } from "../api";
import { downloadFile } from "../download";
import { UploadSimple, FileXls, X, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Bulk Task Upload — shared by Admin (Project Detail) and Brand (Project Detail).
 * Opens a modal that lets the user download a formatted .xlsx template and upload
 * the filled sheet back. All-or-nothing: server rejects the entire file if any row
 * fails validation, and returns row-level error messages.
 */
export default function BulkTaskUpload({ projectId, onSuccess, dataTestid = "bulk-tasks-btn" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setErrors([]); if (inputRef.current) inputRef.current.value = ""; };
  const close = () => { setOpen(false); reset(); };

  const dlTemplate = async () => {
    try {
      await downloadFile(`/projects/${projectId}/tasks/template.xlsx`, "tasks-bulk-template.xlsx");
      toast.success("Template downloaded");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setErrors([]);
    if (f && !f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please choose a .xlsx file");
      e.target.value = ""; setFile(null); return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErrors([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/tasks/bulk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.created} task${data.created === 1 ? "" : "s"} imported`);
      close();
      onSuccess && onSuccess(data.created);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setErrors(detail.errors);
        toast.error(detail.message || "Some rows are invalid");
      } else {
        toast.error(formatErr(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-ghost text-xs flex items-center gap-1.5 text-[#A3E635]"
        data-testid={dataTestid}
      >
        <UploadSimple size={14} weight="bold" /> Bulk upload (.xlsx)
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
          data-testid="bulk-tasks-modal"
          onClick={close}
        >
          <div
            className="glass w-full max-w-lg p-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute top-3 right-3 text-muted hover:text-white"
              data-testid="bulk-tasks-close"
              aria-label="Close"
            ><X size={18} /></button>

            <div className="text-white font-bold text-lg">Bulk upload tasks</div>
            <div className="text-xs text-muted mt-1">
              Download the template, fill your tasks, then upload it back.
              Nothing is created if any row is invalid.
            </div>

            {/* Step 1: template */}
            <div className="mt-4 flex items-center justify-between gap-3 p-3 rounded-2xl bg-white/5 border border-white/5">
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold">1. Download template</div>
                <div className="text-[11px] text-muted">10 columns · Instructions tab included</div>
              </div>
              <button
                type="button"
                onClick={dlTemplate}
                className="btn-ghost text-xs flex items-center gap-1.5 text-[#22D3EE] whitespace-nowrap"
                data-testid="bulk-tasks-template-btn"
              ><FileXls size={14} weight="bold" /> .xlsx template</button>
            </div>

            {/* Step 2: upload */}
            <div className="mt-3 p-3 rounded-2xl bg-white/5 border border-white/5">
              <div className="text-white text-sm font-semibold">2. Upload filled file</div>
              <div className="text-[11px] text-muted mt-0.5">
                Deadline format: <span className="text-white">DD-MM-YYYY</span>.
                Platforms: comma-separated (Instagram, YouTube, LinkedIn, Offline, Other).
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={onFile}
                className="mt-2 block w-full text-xs text-muted file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:bg-[#22D3EE]/10 file:text-[#22D3EE] file:font-bold file:cursor-pointer"
                data-testid="bulk-tasks-file-input"
              />
              {file && (
                <div className="text-[11px] text-white/80 mt-2 flex items-center gap-1.5">
                  <CheckCircle size={12} weight="bold" className="text-[#A3E635]" />
                  <span className="truncate">{file.name} · {(file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>

            {/* Errors */}
            {errors.length > 0 && (
              <div className="mt-3 p-3 rounded-2xl border border-[#EC4899]/40 bg-[#EC4899]/10" data-testid="bulk-tasks-errors">
                <div className="text-[#EC4899] font-bold text-xs flex items-center gap-1.5">
                  <WarningCircle size={14} weight="bold" /> Nothing was imported. Fix these rows and re-upload:
                </div>
                <ul className="mt-2 text-[11px] text-white/90 space-y-1 list-disc pl-4 max-h-40 overflow-y-auto">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={close}
                className="btn-ghost text-sm"
                data-testid="bulk-tasks-cancel"
              >Cancel</button>
              <button
                type="button"
                onClick={upload}
                disabled={!file || busy}
                className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-tasks-upload-btn"
              >
                <UploadSimple size={14} weight="bold" /> {busy ? "Uploading…" : "Upload & create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
